import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * @param {Object} props
 * @param {React.RefObject<HTMLElement>} props.targetRef - Horizontal scroll container to mirror
 * @param {string | number} [props.contentKey] - Changes when inner content size may change
 */
const FloatingScrollbar = ({ targetRef, contentKey }) => {
  const barRef = useRef(null);
  const innerRef = useRef(null);
  const isSyncingRef = useRef(false);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const target = targetRef?.current;
    const bar = barRef.current;
    const inner = innerRef.current;
    if (!target || !bar || !inner) return;

    const getContentWidth = () => {
      const table = target.querySelector("table");
      const content = table || target.firstElementChild || target;
      return Math.max(content.scrollWidth || 0, content.offsetWidth || 0, target.scrollWidth || 0);
    };

    const needsHorizontalScroll = () => getContentWidth() > target.clientWidth + 1;

    /** Native h-scrollbar sits at the container bottom — visible when that edge is in the viewport. */
    const isNativeScrollbarInView = (rect) => rect.bottom <= window.innerHeight;

    const updatePosition = (rect) => {
      setPosition({ left: rect.left, width: rect.width });
    };

    const updateVisibility = () => {
      const rect = target.getBoundingClientRect();
      const inView = rect.bottom > 0 && rect.top < window.innerHeight;
      const canScroll = needsHorizontalScroll();
      const nativeVisible = isNativeScrollbarInView(rect);
      // Floating bar only when the table is in view, scrollable, and its own bar is off-screen.
      const showFloating = inView && canScroll && !nativeVisible;

      setVisible(showFloating);
      target.classList.toggle("floating-scrollbar-target--hide-native", showFloating);
      updatePosition(rect);
    };

    const setInnerWidth = () => {
      inner.style.width = `${getContentWidth()}px`;
    };

    const syncBarFromTarget = () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      bar.scrollLeft = target.scrollLeft;
      isSyncingRef.current = false;
    };

    const syncTargetFromBar = () => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;
      target.scrollLeft = bar.scrollLeft;
      isSyncingRef.current = false;
    };

    setInnerWidth();
    syncBarFromTarget();
    updateVisibility();

    const intersectionObserver = new IntersectionObserver(() => updateVisibility(), {
      root: null,
      threshold: 0,
    });
    intersectionObserver.observe(target);

    const resizeObserver = new ResizeObserver(() => {
      setInnerWidth();
      syncBarFromTarget();
      updateVisibility();
    });
    resizeObserver.observe(target);
    const table = target.querySelector("table");
    if (table) resizeObserver.observe(table);

    target.addEventListener("scroll", syncBarFromTarget);
    bar.addEventListener("scroll", syncTargetFromBar);
    window.addEventListener("scroll", updateVisibility, true);
    window.addEventListener("resize", updateVisibility);

    return () => {
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      target.removeEventListener("scroll", syncBarFromTarget);
      bar.removeEventListener("scroll", syncTargetFromBar);
      window.removeEventListener("scroll", updateVisibility, true);
      window.removeEventListener("resize", updateVisibility);
      target.classList.remove("floating-scrollbar-target--hide-native");
    };
  }, [targetRef, contentKey]);

  return createPortal(
    <div
      className={`floating-scrollbar${visible ? "" : " floating-scrollbar--hidden"}`}
      style={{ left: position.left, width: position.width }}
      aria-hidden={!visible}
    >
      <div ref={barRef} className="floating-scrollbar__track">
        <div ref={innerRef} className="floating-scrollbar__inner" />
      </div>
    </div>,
    document.body,
  );
};

export default FloatingScrollbar;
